import {
  VEHICLE_CLASS_LABELS,
  formatCad,
  parseServiceVariant,
  sortVariants,
  type ParsedVariant,
  type Service,
} from "@shared/modules/x-atelier";
import { formatDuration } from "../lib/dates";
import { VehicleIcon } from "./vehicle-icon";

// Les déclinaisons d'un service sur une seule ligne : une pastille par
// variante, le repère à gauche (silhouette du véhicule, nombre de sièges ou de
// tapis, libellé libre) et le prix à côté, la durée en légende. C'est ce que
// le catalogue, le sélecteur de services et l'éditeur ont en commun.

/** « 124,95 $ », « 90,00 $ / h » au tarif horaire, ou « Sur devis ». */
export function priceLabel(s: Pick<Service, "quoteOnly" | "priceCents" | "pricingUnit">): string {
  if (s.quoteOnly || s.priceCents === null) return "Sur devis";
  return s.pricingUnit === "hourly" ? `${formatCad(s.priceCents)} / h` : formatCad(s.priceCents);
}

/** Ce que la pastille montre comme repère et comme légende. */
export function variantCaption(p: ParsedVariant): string {
  if (p.kind === "vehicle") return p.vehicleClasses.map((c) => VEHICLE_CLASS_LABELS[c]).join(" / ");
  if (p.kind === "seats") return p.extra ? "siège en plus" : p.count === 1 ? "siège" : "sièges";
  if (p.kind === "carpets") return p.extra ? "tapis en plus" : "tapis";
  return "";
}

function Marker({ p }: { p: ParsedVariant }) {
  if (p.kind === "vehicle" && p.vehicleClasses[0]) {
    return (
      <VehicleIcon vehicleClass={p.vehicleClasses[0]} width={30} className="variant-chip__icon" />
    );
  }
  if ((p.kind === "seats" || p.kind === "carpets") && p.count !== null) {
    return (
      <span className="variant-chip__count" aria-hidden="true">
        {p.extra ? `+${p.count}` : p.count}
      </span>
    );
  }
  return <span className="variant-chip__label">{p.label}</span>;
}

interface VariantChipProps {
  service: Service;
  /** Rend la pastille cliquable (modifier, choisir…). */
  onClick?: () => void;
  selected?: boolean;
  /** Masque la durée (sélecteur : la place manque). */
  withDuration?: boolean;
  title?: string;
}

export function VariantChip({
  service,
  onClick,
  selected = false,
  withDuration = true,
  title,
}: VariantChipProps) {
  const p = parseServiceVariant(service);
  const caption = [variantCaption(p), withDuration ? formatDuration(service.durationMin) : ""]
    .filter(Boolean)
    .join(" · ");
  const label = p.label || service.name;
  const classes = [
    "variant-chip",
    `variant-chip--${p.kind}`,
    onClick ? "variant-chip--button" : "",
    selected ? "is-on" : "",
    service.active ? "" : "is-off",
  ]
    .filter(Boolean)
    .join(" ");
  const content = (
    <>
      <Marker p={p} />
      <span className="variant-chip__body">
        <span className="variant-chip__price">{priceLabel(service)}</span>
        {caption ? <span className="variant-chip__caption">{caption}</span> : null}
      </span>
    </>
  );
  const accessible = `${label} : ${priceLabel(service)}${service.active ? "" : " (inactif)"}`;
  if (!onClick) {
    return (
      <span className={classes} title={title ?? accessible} aria-label={accessible}>
        {content}
      </span>
    );
  }
  return (
    <button
      type="button"
      className={classes}
      title={title ?? accessible}
      aria-label={title ?? accessible}
      aria-pressed={selected}
      onClick={onClick}
    >
      {content}
    </button>
  );
}

interface VariantStripProps {
  variants: Service[];
  /** Cliquer une pastille ; absent = pastilles inertes. */
  onPick?: (s: Service) => void;
  selectedIds?: Set<string>;
  withDuration?: boolean;
  ariaLabel?: string;
  /** Titre de chaque pastille (ex. « Modifier la déclinaison SUV »). */
  titleFor?: (s: Service) => string;
}

/** Les variantes d'un groupe, triées dans l'ordre naturel de leur axe. */
export function VariantStrip({
  variants,
  onPick,
  selectedIds,
  withDuration = true,
  ariaLabel,
  titleFor,
}: VariantStripProps) {
  return (
    <div className="variant-strip" role={onPick ? "group" : undefined} aria-label={ariaLabel}>
      {sortVariants(variants).map((v) => (
        <VariantChip
          key={v.id}
          service={v}
          onClick={onPick ? () => onPick(v) : undefined}
          selected={selectedIds?.has(v.id) ?? false}
          withDuration={withDuration}
          title={titleFor?.(v)}
        />
      ))}
    </div>
  );
}
